import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Rocket, Coins, BarChart3 } from "lucide-react";

export const buyKeywordPack = async () => {
  try {
    const r = await api.post("/billing/checkout", { origin_url: window.location.origin, lookup_key: "keyword_pack" });
    window.location.href = r.data.checkout_url;
  } catch (e) { toast.error("Could not start checkout"); }
};

// Shown on 403 QUOTA_EXCEEDED / tier-gated keyword actions: upgrade to E-Commerce or buy the $10 pack.
const MODAL_COPY = {
  credits: { title: "Get exact search volumes", cta: "Upgrade to E-Commerce · 100 lookups/mo", pack: true },
  draft: { title: "Send keywords to the draft builder", cta: "Upgrade to E-Commerce · $79/mo", pack: false },
  agency: { title: "Export & share keyword research", cta: "Upgrade to Agency · CSV export & team sharing", pack: false },
  blog: { title: "Long-form blogs & daily blog campaigns", cta: "Upgrade to E-Commerce · $79/mo", pack: false },
  ppc: { title: "Google + Meta PPC campaigns", cta: "Start your $1 trial", pack: false },
};

export const KeywordUpgradeModal = ({ open, onOpenChange, message, kind = "credits" }) => {
  const copy = MODAL_COPY[kind] || MODAL_COPY.credits;
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-card border-border" data-testid="kw-upgrade-modal">
        <DialogHeader>
          <DialogTitle className="font-display" data-testid="kw-upgrade-title">{copy.title}</DialogTitle>
          <DialogDescription data-testid="kw-upgrade-message">{message || "You're out of search-volume credits."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <Button onClick={() => { onOpenChange(false); navigate("/billing"); }} data-testid="kw-upgrade-ecom-btn" className="w-full bg-primary hover:bg-primary/90 text-white justify-start">
            <Rocket className="h-4 w-4 mr-2" /> {copy.cta}
          </Button>
          {copy.pack && <Button variant="outline" disabled={busy} onClick={async () => { setBusy(true); await buyKeywordPack(); setBusy(false); }} data-testid="kw-buy-pack-btn" className="w-full border-border justify-start">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Coins className="h-4 w-4 mr-2" />} Add 100 credits for $10 · never expire
          </Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const useKeywordCredits = () => {
  const [credits, setCredits] = useState(null);
  const load = useCallback(() => api.get("/keywords/credits").then((r) => setCredits(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return [credits, setCredits, load];
};

// Settings widget: monthly usage + non-expiring add-on balance.
export const KeywordCreditsWidget = () => {
  const [c] = useKeywordCredits();
  if (!c) return null;
  const pct = c.monthly ? Math.min(100, Math.round((c.used / c.monthly) * 100)) : 0;
  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-4 space-y-3" data-testid="kw-credits-widget">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Keyword search-volume credits</p>
        <span className="text-xs font-mono" data-testid="kw-credits-available">{c.available} available</span>
      </div>
      <div>
        <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
          <span data-testid="kw-credits-monthly">{c.monthly ? `${c.used} / ${c.monthly} used this month` : c.in_trial ? "Monthly credits unlock on day 7" : "No monthly credits on this plan"}</span>
          <span data-testid="kw-credits-addon">{c.addon} add-on (never expire)</span>
        </div>
        <div className="h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
      <Button size="sm" variant="outline" onClick={buyKeywordPack} data-testid="kw-credits-buy-btn" className="border-border">
        <Coins className="h-3.5 w-3.5 mr-1.5" /> Add 100 credits · $10
      </Button>
    </div>
  );
};
